import numeral from 'numeral'

import type { Holding } from '../types'

import {
  ALLOCATION_KEYS,
  AnalysisType,
  EquityClassificationTypes,
  EquityClassificationValues,
  FixedIncomeClassificationTypes,
  FixedIncomeClassificationValues,
  STYLE_WEIGHT_KEYS,
} from '../constants/Investments'

type WeightMap = Record<string, number>

// The render band getValidHoldings keeps; the analysis gate reuses it so the
// tab never enables onto an all-zero grid.
const STYLE_WEIGHT_MIN = 80
const STYLE_WEIGHT_MAX = 120
const isInStyleWeightBand = (total: number): boolean =>
  total > STYLE_WEIGHT_MIN && total < STYLE_WEIGHT_MAX

// Coerces so a stringified number counts; null/""/"0"/NaN/0 all mean "no data".
export const isMeaningfulNumber = (value: unknown): boolean => {
  const n = Number(value)
  return Number.isFinite(n) && n !== 0
}

// Classification 0 is UNKNOWN; only 1..9 represent real data.
export const isValidClassification = (value: unknown): boolean => {
  const n = Number(value)
  return Number.isFinite(n) && n >= 1 && n <= 9
}

// Classification only counts when style weights sum to 0, matching
// extractWeight's fallback — otherwise a partial (out-of-band) total that
// getValidHoldings drops would wrongly enable the tab onto an empty grid.
export const holdingHasStyleData = (holding: Holding): boolean => {
  const styleTotal = STYLE_WEIGHT_KEYS.reduce((sum, key) => sum + (Number(holding[key]) || 0), 0)
  return (
    isInStyleWeightBand(styleTotal) ||
    (styleTotal === 0 &&
      (isValidClassification(holding.equity_classification) ||
        isValidClassification(holding.fixed_income_classification)))
  )
}

export const holdingHasAllocationData = (holding: Holding): boolean =>
  ALLOCATION_KEYS.some((key) => isMeaningfulNumber(holding[key]))

// Enriched market value from Custodian; falls back to the raw feed value.
export const getHoldingMarketValue = (holding: Holding): number =>
  holding.calculated_market_value ?? holding.market_value ?? 0

// The cost basis to measure gain/loss against. `cost_basis` is user-edited and
// persists in Abacus; it is null until someone sets it. `purchase_price` is the
// cumulative feed cost for all shares and is a valid stand-in. A basis of 0 is a
// real value (e.g. a gifted holding) and must be kept, so only null/undefined/
// non-finite is treated as "no basis known".
//
// Returns null when neither value is known — callers must NOT coerce that to 0,
// which would fabricate a 100% gain (the MEW-4073 symptom: market_value became
// enriched/non-zero while cost_basis stayed null).
export const getHoldingCostBasis = (holding: Holding): number | null => {
  if (Number.isFinite(holding.cost_basis)) return holding.cost_basis
  if (Number.isFinite(holding.purchase_price)) return holding.purchase_price
  return null
}

export interface HoldingGainLoss {
  available: boolean
  basis: number
  gainLoss: number
  gainLossPercentage: number
}

// Single source of truth for a holding's gain/loss. When no cost basis is known,
// `available` is false and callers should render "—" rather than a fake 0/100%.
export const getHoldingGainLoss = (holding: Holding): HoldingGainLoss => {
  const basis = getHoldingCostBasis(holding)
  if (basis === null) {
    return { available: false, basis: 0, gainLoss: 0, gainLossPercentage: 0 }
  }
  const gainLoss = getHoldingMarketValue(holding) - basis
  return {
    available: true,
    basis,
    gainLoss,
    gainLossPercentage: basis === 0 ? 0 : (gainLoss / basis) * 100,
  }
}

export const createGroups = (classificationObj: Record<number, string>, groupSize = 3) => {
  const keys = Object.keys(classificationObj)
    .map(Number)
    .filter((k) => k !== 0)
    .sort((a, b) => a - b)

  const groups = []
  for (let i = 0; i < keys.length; i += groupSize) {
    groups.push(keys.slice(i, i + groupSize).map((key) => classificationObj[key]))
  }
  return groups
}

export const bondGroups = createGroups(FixedIncomeClassificationValues)
export const stockGroups = createGroups(EquityClassificationValues)

export const analysisChartData = (holdings: Holding[]) => {
  const stocks = getValidHoldings(holdings, extractStockWeight)
  const bonds = getValidHoldings(holdings, extractBondWeight)

  const processGroupData = (group: Holding[], extractValueFn: (holding: Holding) => WeightMap) => {
    const totalValue = group.reduce((acc, holding) => acc + getHoldingValue(holding), 0)
    const groupValues = group.reduce((acc, holding) => {
      const value = extractValueFn(holding)
      Object.keys(value).forEach((key) => (acc[key] = (acc[key] || 0) + value[key]))
      return acc
    }, {} as WeightMap)

    return Object.fromEntries(
      Object.entries(groupValues).map(([key, value]) => [key, (value / totalValue) * 100]),
    )
  }

  const data = {
    stocks: processGroupData(stocks, extractStockValueFromWeight),
    bonds: processGroupData(bonds, extractBondValueFromWeight),
  }

  return data
}

export const analysisDetailsData = (holdings: Holding[]) => {
  const stocks = holdings.filter((holding) => !isNaN(holding.equity_classification))
  const bonds = holdings.filter((holding) => !isNaN(holding.fixed_income_classification))

  const aggregateValues = (
    holdings: Holding[],
    extractValueFn: (holding: Holding) => WeightMap,
    classificationTypes: Record<string, number>,
  ) => {
    return holdings.reduce((acc, holding) => {
      const values = extractValueFn(holding)
      Object.keys(classificationTypes).forEach((sector) => {
        acc[sector] = (acc[sector] || 0) + (values[sector] || 0)
      })
      return acc
    }, {} as WeightMap)
  }

  return {
    stocks: aggregateValues(stocks, extractStockValueFromWeight, EquityClassificationTypes),
    bonds: aggregateValues(bonds, extractBondValueFromWeight, FixedIncomeClassificationTypes),
  }
}

export const calculateWeightedValue = (weights: WeightMap, value: number): WeightMap => {
  return Object.fromEntries(
    Object.entries(weights).map(([key, weight]) => [key, (weight * value) / 100]),
  )
}

const extractBondValueFromWeight = (bond: Holding) =>
  extractValueFromWeight(bond, extractBondWeight, FixedIncomeClassificationValues)

export const extractBondWeight = (bond: Holding): WeightMap => {
  const classificationKey = bond.fixed_income_classification
  return {
    [FixedIncomeClassificationValues[
      classificationKey as keyof typeof FixedIncomeClassificationValues
    ]]: 100,
  }
}

const extractStockValueFromWeight = (stock: Holding) =>
  extractValueFromWeight(stock, extractStockWeight, EquityClassificationValues)

export const extractStockWeight = (stock: Holding): WeightMap => {
  const {
    large_core_weight,
    large_growth_weight,
    large_value_weight,
    mid_core_weight,
    mid_growth_weight,
    mid_value_weight,
    small_core_weight,
    small_growth_weight,
    small_value_weight,
    unknown_weight,
    equity_classification,
  } = stock

  const data: WeightMap = {
    LARGE_CORE: large_core_weight ?? 0,
    LARGE_GROWTH: large_growth_weight ?? 0,
    LARGE_VALUE: large_value_weight ?? 0,
    MID_CORE: mid_core_weight ?? 0,
    MID_GROWTH: mid_growth_weight ?? 0,
    MID_VALUE: mid_value_weight ?? 0,
    SMALL_CORE: small_core_weight ?? 0,
    SMALL_GROWTH: small_growth_weight ?? 0,
    SMALL_VALUE: small_value_weight ?? 0,
    UNKNOWN: unknown_weight ?? 0,
  }

  if (sumValues(data) === 0 && equity_classification !== undefined) {
    const key =
      EquityClassificationValues[equity_classification as keyof typeof EquityClassificationValues]
    return { [key]: 100 }
  }

  return data
}

export const extractValueFromWeight = (
  holding: Holding,
  extractWeightFn: (h: Holding) => WeightMap,
  classificationValues: Record<string, string>,
): WeightMap => {
  const weights = extractWeightFn(holding)
  const value = getHoldingValue(holding)
  const weightedValues = calculateWeightedValue(weights, value)

  if (sumValues(weightedValues) === 0) {
    const classificationKey = String(
      holding.equity_classification ?? holding.fixed_income_classification,
    )

    if (classificationKey in classificationValues) {
      return { [classificationValues[classificationKey]]: value }
    } else {
      console.error(`Invalid classification key: ${classificationKey}`)
    }
  }

  return weightedValues
}

export const formatKey = (key: string): string =>
  key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

export const formatPercent = (value: number): string =>
  value >= 1 ? `${numeral(value).format('0')}%` : '< 1%'

export const getHoldingValue = (holding: Holding): number =>
  holding.calculated_market_value ?? holding.market_value

export const getValidHoldings = (
  holdings: Holding[],
  extractWeightFn: (holding: Holding) => WeightMap,
): Holding[] => {
  return holdings.filter((holding) => {
    const weight = extractWeightFn(holding)
    return isInStyleWeightBand(sumValues(weight))
  })
}

export const mapDataToPercentageGrid = (
  data: Record<AnalysisType, Record<string, number>>,
  analysisType: AnalysisType,
): string[][] => {
  const groups = analysisType === AnalysisType.Stocks ? stockGroups : bondGroups

  const selected = data[analysisType]
  if (!selected || Object.keys(selected).length === 0)
    return groups.map((group) => group.map(() => '0%'))

  return groups.map((group) => group.map((key) => formatPercent(selected[key] ?? 0)))
}

export const sumValues = (obj: WeightMap): number =>
  Object.values(obj).reduce((acc, val) => acc + val, 0)
